import './App.css';
import { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import SlimLoadingBar from './components/SlimLoadingBar';
import HomePage from './pages/HomePage';
import TransactionDetailsPage from './pages/TransactionDetailsPage';
import TransactionsListPage from './pages/TransactionsListPage';
import LookupDetailPage from './pages/LookupDetailPage';
import ExplorerRedirect from './components/explorer/ExplorerRedirect';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import MaintenancePage from './pages/MaintenancePage';
import ComingSoonPage from './pages/ComingSoonPage';
import ContactPage from './pages/ContactPage';
import StaticPage from './pages/StaticPage';
import { useSiteConfig } from './context/SiteConfigContext';

const THEME_STORAGE_KEY = 'polyrion-theme-preference';
const AUTH_STANDALONE_PATHS = new Set(['/login', '/register']);

function AppContent() {
  const location = useLocation();
  const { loading: siteLoading, isMaintenance } = useSiteConfig();
  const isAuthStandalone = AUTH_STANDALONE_PATHS.has(location.pathname);

  const [theme, setTheme] = useState(() => {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    return storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'system'
      ? storedTheme
      : 'system';
  });

  const activeTheme = useMemo(() => {
    if (theme !== 'system') {
      return theme;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', activeTheme);
  }, [activeTheme]);

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    if (theme !== 'system') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => {
      document.documentElement.setAttribute('data-theme', event.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  useEffect(() => {
    if (!isAuthStandalone) {
      return undefined;
    }
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#eef1f8';
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, [isAuthStandalone]);

  if (siteLoading) {
    return <div className="App App--site-loading" aria-busy="true" />;
  }

  if (isMaintenance) {
    return <MaintenancePage />;
  }

  return (
    <div className={`App${isAuthStandalone ? ' App--auth-standalone' : ''}`}>
      <SlimLoadingBar />
      {!isAuthStandalone ? (
        <Header theme={theme} setTheme={setTheme} activeTheme={activeTheme} />
      ) : null}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/transactions" element={<TransactionsListPage />} />
        <Route path="/explorer" element={<ExplorerRedirect />} />
        <Route path="/lookup" element={<LookupDetailPage />} />
        <Route path="/tx/:signature" element={<TransactionDetailsPage />} />
        <Route path="/defi" element={<ComingSoonPage />} />
        <Route path="/leaderboard" element={<ComingSoonPage />} />
        <Route path="/resources" element={<ComingSoonPage />} />
        <Route path="/developers" element={<ComingSoonPage />} />
        <Route path="/addresses" element={<ComingSoonPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/about" element={<StaticPage slug="about" />} />
        <Route path="/terms" element={<StaticPage slug="terms" />} />
        <Route path="/privacy" element={<StaticPage slug="privacy" />} />
        <Route path="/branding" element={<StaticPage slug="branding" />} />
        <Route path="/bug-bounty" element={<StaticPage slug="bug-bounty" />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
      </Routes>
      {!isAuthStandalone ? <Footer /> : null}
    </div>
  );
}

function App() {
  return (
    <AppContent />
  );
}

export default App;
